import {
    Avatar, Box, Button, Chip, Divider, IconButton,
    Input, Modal, Sheet, Stack, Typography, CircularProgress,
} from "@mui/joy";
import { useContext, useEffect, useRef, useState } from "react";
import { LayoutContext } from "../../context";
import { motion, AnimatePresence } from "framer-motion";
import { BASE_URL } from "../../api";

import CloseRoundedIcon           from "@mui/icons-material/CloseRounded";
import ExitToAppRoundedIcon        from "@mui/icons-material/ExitToAppRounded";
import PersonRoundedIcon           from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon            from "@mui/icons-material/EmailRounded";
import EditRoundedIcon             from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon    from "@mui/icons-material/DeleteOutlineRounded";
import CameraAltRoundedIcon        from "@mui/icons-material/CameraAltRounded";
import CheckRoundedIcon            from "@mui/icons-material/CheckRounded";
import BadgeRoundedIcon            from "@mui/icons-material/BadgeRounded";

import api from "../../api";

// ─── API helpers ──────────────────────────────────────────────────────────────

async function APIUpdateProfile(data) {
    const res = await api.patch("/api/auth/profile/", data);
    return res.data;
}

async function APIUploadAvatar(file) {
    const form = new FormData();
    form.append("avatar", file);
    const res = await api.patch("/api/auth/profile/", form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
}

async function APIDeleteAvatar() {
    const res = await api.patch("/api/auth/profile/", { avatar: null });
    return res.data;
}

// ─── ProfileRow ───────────────────────────────────────────────────────────────

function ProfileRow({ icon, label, value, editing, inputValue, onChange, type = "text" }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.1 }}>
            <Box sx={{
                mt: 0.2, width: 32, height: 32, borderRadius: "10px",
                bgcolor: "rgba(87,42,195,0.07)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, color: "#572AC3",
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 600, mb: 0.3, fontSize: "0.68rem" }}>
                    {label}
                </Typography>
                {editing && onChange ? (
                    <Input
                        size="sm"
                        type={type}
                        value={inputValue ?? ""}
                        onChange={e => onChange(e.target.value)}
                        sx={{ borderRadius: "10px", fontSize: "0.88rem" }}
                    />
                ) : (
                    <Typography level="body-sm" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                        {value || "—"}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

// ─── AvatarEditor ─────────────────────────────────────────────────────────────

function AvatarEditor({ profile, onAvatarChange }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [deleting,  setDeleting]  = useState(false);

    const avatarSrc = profile?.avatar ? `${BASE_URL}${profile.avatar}` : null;
    const initials  = profile
        ? `${profile.first_name?.[0] ?? ""}${profile.last_name?.[0] ?? ""}`.toUpperCase()
        : "U";

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const data = await APIUploadAvatar(file);
            onAvatarChange(data);
        } catch {}
        finally { setUploading(false); e.target.value = ""; }
    };

    const handleDelete = async () => {
        if (!window.confirm("Удалить аватарку?")) return;
        setDeleting(true);
        try {
            const data = await APIDeleteAvatar();
            onAvatarChange(data);
        } catch {}
        finally { setDeleting(false); }
    };

    return (
        <Stack direction={'row'} sx={{ gap: 1.5 }}>
            <Box sx={{ position: "relative" }}>
                <Avatar
                    src={avatarSrc || undefined}
                    sx={{
                        width: 84, height: 84,
                        fontSize: "1.8rem", fontWeight: 700,
                        background: "linear-gradient(135deg, #572AC3 0%, #7C3AED 100%)",
                        color: "white",
                        boxShadow: "0 4px 16px rgba(87,42,195,0.3)",
                    }}
                >
                    {initials}
                </Avatar>

                {/* Кнопка загрузки поверх аватарки */}
                <Box
                    onClick={() => fileRef.current?.click()}
                    sx={{
                        position: "absolute", inset: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(0,0,0,0)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", transition: "background 0.2s",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.4)" },
                        "&:hover .cam-icon": { opacity: 1 },
                    }}
                >
                    {uploading
                        ? <CircularProgress size="sm" sx={{ color: "white" }} />
                        : <CameraAltRoundedIcon
                            className="cam-icon"
                            sx={{ fontSize: 22, color: "white", opacity: 0, transition: "opacity 0.2s" }}
                          />
                    }
                </Box>

                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleUpload}
                />
            </Box>

            {/* Имя */}
            <Box sx={{ textAlign: "flex-start" }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", lineHeight: 1.2, letterSpacing: "-0.3px" }}>
                    {profile ? `${profile.first_name} ${profile.last_name}` : "Пользователь"}
                </Typography>
                <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.3 }}>
                    {profile?.email}
                </Typography>
            
                {/* Кнопки аватарки */}
                <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                    <Button
                        size="sm" variant="soft" color="primary"
                        startDecorator={<CameraAltRoundedIcon sx={{ fontSize: 14 }} />}
                        onClick={() => fileRef.current?.click()}
                        loading={uploading}
                        sx={{ borderRadius: 10, fontSize: "0.75rem", height: 28 }}
                    >
                        {avatarSrc ? "Изменить" : "Добавить фото"}
                    </Button>
                    {avatarSrc && (
                        <Button
                            size="sm" variant="soft" color="danger"
                            startDecorator={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                            onClick={handleDelete}
                            loading={deleting}
                            sx={{ borderRadius: 10, fontSize: "0.75rem", height: 28 }}
                        >
                            Убрать фото
                        </Button>
                    )}
                </Stack>
            </Box>
        </Stack>
    );
}

// ─── ModalProfile ─────────────────────────────────────────────────────────────

export default function ModalProfile() {
    const { profile, setProfile, logout, modalProfile, setModalProfile } = useContext(LayoutContext);

    const [editing, setEditing] = useState(false);
    const [saving,  setSaving]  = useState(false);
    const [form,    setForm]    = useState({});
    const [errors,  setErrors]  = useState({});

    const open = modalProfile ?? false;

    useEffect(() => {
        if (open) {
            setForm({
                first_name: profile?.first_name ?? "",
                last_name:  profile?.last_name  ?? "",
                email:      profile?.email      ?? "",
            });
            setEditing(false);
            setErrors({});
        }
    }, [open]);

    const handleSave = async () => {
        const errs = {};
        if (!form.first_name?.trim()) errs.first_name = "Введите имя";
        if (!form.last_name?.trim())  errs.last_name  = "Введите фамилию";
        if (!form.email?.trim())      errs.email      = "Введите email";
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSaving(true);
        try {
            const data = await APIUpdateProfile({
                first_name: form.first_name.trim(),
                last_name:  form.last_name.trim(),
                email:      form.email.trim(),
            });
            setProfile(prev => ({ ...prev, ...data }));
            setEditing(false);
        } catch (e) {
            const msg = e?.response?.data;
            if (msg?.email) setErrors({ email: "Этот email уже используется" });
        } finally {
            setSaving(false);
        }
    };

    const handleAvatarChange = (data) => {
        setProfile(prev => ({ ...prev, avatar: data.avatar }));
    };

    const profileTypeLabels = {
        superadmin: { label: "Супер-админ", color: "#ef4444" },
        developer:  { label: "Разработчик", color: "#6366f1" },
        manager:    { label: "Менеджер",    color: "#10b981" },
        standard:   { label: "Пользователь", color: "#572AC3" },
    };
    const typeInfo = profileTypeLabels[profile?.profile_type] ?? profileTypeLabels.standard;

    return (
        <AnimatePresence>
            {open && (
                <Modal
                    open
                    onClose={() => setModalProfile(false)}
                    sx={{
                        zIndex: 1300,
                        display: "flex",
                        alignItems: { xs: "flex-end", sm: "center" },
                        justifyContent: "center",
                    }}
                    slotProps={{
                        backdrop: {
                            sx: {
                                backdropFilter: "blur(8px)",
                                backgroundColor: "rgba(0,0,0,0.3)",
                            },
                        },
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 60, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.97 }}
                        transition={{ type: "spring", stiffness: 340, damping: 30 }}
                        style={{ width: "100%", maxWidth: 420, outline: "none" }}
                    >
                        <Sheet sx={{
                            width: "100%",
                            borderRadius: { xs: "20px 20px 0 0", sm: "24px" },
                            overflow: "hidden",
                            boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
                            border: "1.5px solid",
                            borderColor: "divider",
                        }}>
                            {/* ── Шапка ── */}
                            <Stack direction={'column'} alignItems={'flex-start'} sx={{
                                px: 3, pt: 3, pb: 3,
                                background: "linear-gradient(135deg, #f2f0fd 0%, #ede9fe 100%)",
                                borderBottom: "1px solid", borderColor: "divider",
                                position: "relative",
                            }}>
                                <IconButton
                                    size="sm" variant="plain" color="neutral"
                                    onClick={() => setModalProfile(false)}
                                    sx={{
                                        position: "absolute", top: 12, right: 12,
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
                                    }}
                                >
                                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>

                                <AvatarEditor profile={profile} onAvatarChange={handleAvatarChange} />

                                {/* Тип профиля */}
                                <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                                    <Chip size="sm"
                                        sx={{
                                            bgcolor: `${typeInfo.color}15`,
                                            color: typeInfo.color,
                                            fontWeight: 700,
                                            fontSize: "0.65rem",
                                            border: `1px solid ${typeInfo.color}25`,
                                            height: 20,
                                        }}
                                    >
                                        {typeInfo.label}
                                    </Chip>
                                </Box>
                            </Stack>

                            {/* ── Поля профиля ── */}
                            <Box sx={{ px: 3, py: 1.5 }}>
                                <ProfileRow
                                    icon={<PersonRoundedIcon fontSize="small" />}
                                    label="Имя"
                                    value={profile?.first_name}
                                    editing={editing}
                                    inputValue={form.first_name}
                                    onChange={v => { setForm(p => ({ ...p, first_name: v })); setErrors(p => ({ ...p, first_name: "" })); }}
                                />
                                {errors.first_name && (
                                    <Typography sx={{ fontSize: "0.72rem", color: "danger.500", mt: -0.5, mb: 0.5, pl: "44px" }}>
                                        {errors.first_name}
                                    </Typography>
                                )}
                                <Divider />

                                <ProfileRow
                                    icon={<BadgeRoundedIcon fontSize="small" />}
                                    label="Фамилия"
                                    value={profile?.last_name}
                                    editing={editing}
                                    inputValue={form.last_name}
                                    onChange={v => { setForm(p => ({ ...p, last_name: v })); setErrors(p => ({ ...p, last_name: "" })); }}
                                />
                                {errors.last_name && (
                                    <Typography sx={{ fontSize: "0.72rem", color: "danger.500", mt: -0.5, mb: 0.5, pl: "44px" }}>
                                        {errors.last_name}
                                    </Typography>
                                )}
                                <Divider />

                                <ProfileRow
                                    icon={<EmailRoundedIcon fontSize="small" />}
                                    label="Email"
                                    value={profile?.email}
                                    editing={editing}
                                    inputValue={form.email}
                                    onChange={v => { setForm(p => ({ ...p, email: v })); setErrors(p => ({ ...p, email: "" })); }}
                                    type="email"
                                />
                                {errors.email && (
                                    <Typography sx={{ fontSize: "0.72rem", color: "danger.500", mt: -0.5, mb: 0.5, pl: "44px" }}>
                                        {errors.email}
                                    </Typography>
                                )}
                            </Box>

                            {/* ── Кнопки ── */}
                            <Box sx={{ px: 3, pb: 3, pt: 0.5 }}>
                                <AnimatePresence mode="wait">
                                    {!editing ? (
                                        <motion.div key="view"
                                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}
                                        >
                                            <Stack spacing={1}>
                                                <Button
                                                    fullWidth variant="soft" color="primary"
                                                    startDecorator={<EditRoundedIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => setEditing(true)}
                                                    sx={{ borderRadius: "14px", fontWeight: 700 }}
                                                >
                                                    Редактировать профиль
                                                </Button>
                                                <Button
                                                    fullWidth variant="soft" color="danger"
                                                    startDecorator={<ExitToAppRoundedIcon />}
                                                    onClick={() => { setModalProfile(false); logout(); }}
                                                    sx={{ borderRadius: "14px", fontWeight: 700 }}
                                                >
                                                    Выйти из аккаунта
                                                </Button>
                                            </Stack>
                                        </motion.div>
                                    ) : (
                                        <motion.div key="edit"
                                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}
                                        >
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    variant="outlined" color="neutral" fullWidth
                                                    onClick={() => { setEditing(false); setErrors({}); }}
                                                    sx={{ borderRadius: "14px" }}
                                                >
                                                    Отмена
                                                </Button>
                                                <Button
                                                    variant="solid" color="primary" fullWidth
                                                    loading={saving}
                                                    startDecorator={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
                                                    onClick={handleSave}
                                                    sx={{ borderRadius: "14px", fontWeight: 700 }}
                                                >
                                                    Сохранить
                                                </Button>
                                            </Stack>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </Box>
                        </Sheet>
                    </motion.div>
                </Modal>
            )}
        </AnimatePresence>
    );
}